import React, { useState } from 'react';
import { cashFlowForecastData } from '../../data/cashFlowForecastData';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Activity, DollarSign, Calendar, ArrowUp, ArrowDown, AlertTriangle, Download, Sliders, BarChart2, PieChart as PieChartIcon } from 'lucide-react';

const CashFlowForecastDashboard = () => {
  const { monthlyCashFlowForecasts, cashFlowScenarios, cashFlowKPIs, sensitivityAnalysis } = cashFlowForecastData;
  const [activeScenario, setActiveScenario] = useState('cf-scenario-001');
  const [activeFactor, setActiveFactor] = useState('sa-001');

  // Format currency
  const formatCurrency = (amount, currency = 'HKD') => {
    return new Intl.NumberFormat('zh-HK', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    const [year, month] = dateString.split('-');
    return `${year}年${month}月`;
  };

  // Get selected scenario data
  const selectedScenario = cashFlowScenarios.find(scenario => scenario.scenario_id === activeScenario);

  // Prepare data for cash flow projection chart
  const projectionChartData = selectedScenario?.monthly_projections.map(projection => ({
    month: formatDate(projection.month),
    netCashFlow: projection.net_cash_flow,
    endingBalance: projection.ending_balance,
  }));

  // Prepare data for monthly cash inflow/outflow chart
  const latestForecast = monthlyCashFlowForecasts[0];
  const inOutflowData = [
    {
      name: '現金流入',
      value: latestForecast.cash_inflows.reduce((sum, inflow) => sum + inflow.amount, 0),
    },
    {
      name: '現金流出',
      value: latestForecast.cash_outflows.reduce((sum, outflow) => sum + outflow.amount, 0),
    },
    {
      name: '淨現金流',
      value: latestForecast.net_cash_flow,
    },
  ];

  // Prepare data for inflow breakdown
  const inflowBreakdownData = latestForecast.cash_inflows.map(inflow => ({
    name: inflow.category === 'sales_revenue' ? '銷售收入' : 
          inflow.category === 'accounts_receivable' ? '應收賬款' : 
          inflow.category === 'investment_income' ? '投資收益' : inflow.category,
    value: inflow.amount,
  }));

  // Prepare data for outflow breakdown
  const outflowBreakdownData = latestForecast.cash_outflows.map(outflow => ({
    name: outflow.category === 'raw_materials' ? '原材料' : 
          outflow.category === 'payroll' ? '薪資' : 
          outflow.category === 'rent_utilities' ? '租金和水電' : 
          outflow.category === 'taxes' ? '稅款' : 
          outflow.category === 'loan_payments' ? '貸款還款' : outflow.category,
    value: outflow.amount,
  }));

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <h2 className="text-2xl font-bold mb-4">現金流預測儀表板</h2>
          <p className="text-gray-600 mb-4">
            查看未來現金流預測、不同情境分析和關鍵績效指標，以優化財務決策和規劃。
          </p>
          <div className="relative h-60 rounded-lg overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="Cash Flow Forecast"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/80 to-purple-900/50 flex items-end p-6">
              <h3 className="text-white text-2xl font-bold">準確的現金流預測是財務穩定的關鍵</h3>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {cashFlowKPIs.map((kpi) => (
            <Card key={kpi.kpi_id} className={`shadow-md ${kpi.status === 'excellent' ? 'border-l-4 border-green-500' : kpi.status === 'good' ? 'border-l-4 border-blue-500' : 'border-l-4 border-yellow-500'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">{kpi.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-2xl font-bold">
                      {kpi.unit === 'ratio' ? kpi.current_value.toFixed(2) : 
                       kpi.unit === 'days' ? `${kpi.current_value}天` : 
                       kpi.unit === 'months' ? `${kpi.current_value}個月` : 
                       kpi.unit === 'HKD' ? formatCurrency(kpi.current_value, 'HKD') : 
                       kpi.current_value}
                    </div>
                    <p className="text-xs text-gray-500">目標: {kpi.unit === 'ratio' ? kpi.target_value.toFixed(2) : 
                       kpi.unit === 'days' ? `${kpi.target_value}天` : 
                       kpi.unit === 'months' ? `${kpi.target_value}個月` : 
                       kpi.unit === 'HKD' ? formatCurrency(kpi.target_value, 'HKD') : 
                       kpi.target_value}</p>
                  </div>
                  <div className={`p-2 rounded-full ${kpi.trend === 'improving' ? 'bg-green-100' : kpi.trend === 'stable' ? 'bg-blue-100' : 'bg-yellow-100'}`}>
                    {kpi.trend === 'improving' ? <TrendingUp className="h-5 w-5 text-green-600" /> : 
                     kpi.trend === 'stable' ? <Activity className="h-5 w-5 text-blue-600" /> : 
                     <TrendingUp className="h-5 w-5 text-yellow-600" />}
                  </div>
                </div>
                <Progress 
                  value={(kpi.current_value / kpi.target_value) * 100} 
                  className="h-1 mt-2" 
                  indicatorClassName={kpi.status === 'excellent' ? 'bg-green-500' : kpi.status === 'good' ? 'bg-blue-500' : 'bg-yellow-500'}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-xl">現金流情境分析</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs
              defaultValue={activeScenario}
              value={activeScenario}
              onValueChange={setActiveScenario}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3 mb-6">
                {cashFlowScenarios.map(scenario => (
                  <TabsTrigger key={scenario.scenario_id} value={scenario.scenario_id}>
                    {scenario.scenario_name}
                  </TabsTrigger>
                ))}
              </TabsList>

              {cashFlowScenarios.map(scenario => (
                <TabsContent key={scenario.scenario_id} value={scenario.scenario_id}>
                  <div className="mb-4">
                    <p className="text-gray-600">{scenario.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500">預測期間</p>
                        <p className="font-medium">{formatDate(scenario.period_start)} - {formatDate(scenario.period_end)}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500">年度現金流入</p>
                        <p className="font-medium">{formatCurrency(scenario.annual_summary.total_inflows, scenario.currency)}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500">年度現金流出</p>
                        <p className="font-medium">{formatCurrency(scenario.annual_summary.total_outflows, scenario.currency)}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500">投資回報率</p>
                        <p className={`font-medium ${scenario.annual_summary.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {scenario.annual_summary.roi.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-medium mb-2">淨現金流預測</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={projectionChartData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="netCashFlow" fill="#82ca9d" name="淨現金流" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium mb-2">期末餘額預測</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={projectionChartData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Area type="monotone" dataKey="endingBalance" fill="#8884d8" stroke="#8884d8" name="期末餘額" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 rounded-lg bg-gray-50">
                    <div className="flex items-center mb-2">
                      <div className={`p-2 rounded-full mr-2 ${scenario.annual_summary.net_annual_flow >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                        {scenario.annual_summary.net_annual_flow >= 0 ? 
                          <ArrowUp className="h-5 w-5 text-green-600" /> : 
                          <ArrowDown className="h-5 w-5 text-red-600" />}
                      </div>
                      <div>
                        <p className="font-medium">年度淨現金流</p>
                        <p className={`text-lg font-bold ${scenario.annual_summary.net_annual_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(scenario.annual_summary.net_annual_flow, scenario.currency)}
                        </p>
                      </div>
                    </div>
                    {scenario.annual_summary.net_annual_flow < 0 && (
                      <div className="mt-2 flex items-center text-red-600 text-sm">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        <span>警告：預計年度現金流為負數，可能需要額外融資或成本削減措施。</span>
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="shadow-md">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl">{formatDate(latestForecast.period)}現金流預測</CardTitle>
              <Badge variant="outline">
                預測於 {latestForecast.forecast_date}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">起始餘額</p>
                <p className="text-xl font-bold">{formatCurrency(latestForecast.starting_balance, latestForecast.currency)}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">預計期末餘額</p>
                <p className="text-xl font-bold">{formatCurrency(latestForecast.ending_balance, latestForecast.currency)}</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-medium mb-2">現金流入/流出概覽</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={inOutflowData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-blue-50 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-blue-100 mr-3">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">淨現金流</p>
                    <p className={`text-lg font-bold ${latestForecast.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(latestForecast.net_cash_flow, latestForecast.currency)}
                    </p>
                  </div>
                </div>
                <div className={`p-3 rounded-full ${latestForecast.net_cash_flow >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  {latestForecast.net_cash_flow >= 0 ? 
                    <ArrowUp className="h-6 w-6 text-green-600" /> : 
                    <ArrowDown className="h-6 w-6 text-red-600" />}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-xl">現金流明細分析</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="inflows" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="inflows">現金流入</TabsTrigger>
                <TabsTrigger value="outflows">現金流出</TabsTrigger>
              </TabsList>

              <TabsContent value="inflows">
                <div className="space-y-4">
                  {latestForecast.cash_inflows.map((inflow, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <div className="p-2 rounded-full mr-3" style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}>
                          <ArrowUp className="h-5 w-5" style={{ color: COLORS[index % COLORS.length] }} />
                        </div>
                        <div>
                          <div className="font-medium">
                            {inflow.category === 'sales_revenue' ? '銷售收入' : 
                             inflow.category === 'accounts_receivable' ? '應收賬款' : 
                             inflow.category === 'investment_income' ? '投資收益' : inflow.category}
                          </div>
                          <div className="text-xs text-gray-500">{inflow.notes}</div>
                        </div>
                      </div>
                      <div className="font-bold">
                        {formatCurrency(inflow.amount, latestForecast.currency)}
                      </div>
                    </div>
                  ))}

                  <div className="mt-6">
                    <h4 className="text-lg font-medium mb-2">流入類別分佈</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={inflowBreakdownData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {inflowBreakdownData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="outflows">
                <div className="space-y-4">
                  {latestForecast.cash_outflows.map((outflow, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <div className="p-2 rounded-full mr-3" style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}>
                          <ArrowDown className="h-5 w-5" style={{ color: COLORS[index % COLORS.length] }} />
                        </div>
                        <div>
                          <div className="font-medium">
                            {outflow.category === 'raw_materials' ? '原材料' : 
                             outflow.category === 'payroll' ? '薪資' : 
                             outflow.category === 'rent_utilities' ? '租金和水電' : 
                             outflow.category === 'taxes' ? '稅款' : 
                             outflow.category === 'loan_payments' ? '貸款還款' : outflow.category}
                          </div>
                          <div className="text-xs text-gray-500">{outflow.notes}</div>
                        </div>
                      </div>
                      <div className="font-bold">
                        {formatCurrency(outflow.amount, latestForecast.currency)}
                      </div>
                    </div>
                  ))}

                  <div className="mt-6">
                    <h4 className="text-lg font-medium mb-2">流出類別分佈</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={outflowBreakdownData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {outflowBreakdownData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {monthlyCashFlowForecasts.map((forecast) => (
          <Card key={forecast.forecast_id} className="shadow-md">
            <CardHeader className="pb-2">
              <div className="flex justify-between">
                <CardTitle>{formatDate(forecast.period)}預測</CardTitle>
                <Badge variant="outline">
                  {forecast.forecast_date}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="text-2xl font-bold">{formatCurrency(forecast.net_cash_flow, forecast.currency)}</div>
                  <p className="text-sm text-gray-500">淨現金流</p>
                </div>
                <div className={`p-3 rounded-full ${forecast.net_cash_flow >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  {forecast.net_cash_flow >= 0 ? 
                    <ArrowUp className="h-6 w-6 text-green-600" /> : 
                    <ArrowDown className="h-6 w-6 text-red-600" />}
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>起始餘額</span>
                  <span>{formatCurrency(forecast.starting_balance, forecast.currency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>現金流入</span>
                  <span>{formatCurrency(forecast.cash_inflows.reduce((sum, inflow) => sum + inflow.amount, 0), forecast.currency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>現金流出</span>
                  <span>{formatCurrency(forecast.cash_outflows.reduce((sum, outflow) => sum + outflow.amount, 0), forecast.currency)}</span>
                </div>
                <div className="h-px bg-gray-200 my-2"></div>
                <div className="flex justify-between text-sm font-medium">
                  <span>期末餘額</span>
                  <span>{formatCurrency(forecast.ending_balance, forecast.currency)}</span>
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-center">
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center">
                  <Calendar className="h-4 w-4 mr-1" /> 查看詳細預測
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sensitivity Analysis Section */}
      <div className="mb-8">
        <Card className="shadow-md">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl">
                <div className="flex items-center">
                  <Sliders className="h-5 w-5 mr-2" />
                  敏感度分析
                </div>
              </CardTitle>
              <Badge variant="outline" className="bg-blue-50">
                影響現金流的關鍵因素
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              此分析顯示不同因素變化對公司現金流的潛在影響，幫助識別關鍵風險和機會。
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
              {sensitivityAnalysis.map((factor) => (
                <button
                  key={factor.factor_id}
                  onClick={() => setActiveFactor(factor.factor_id)}
                  className={`p-3 rounded-lg text-left transition-all ${activeFactor === factor.factor_id ? 'bg-blue-100 border-blue-300 border' : 'bg-gray-50 hover:bg-gray-100'}`}
                >
                  <p className="font-medium">{factor.factor_name}</p>
                  <p className="text-sm text-gray-500">
                    基準值: {factor.base_value}{factor.unit}
                  </p>
                </button>
              ))}
            </div>

            {sensitivityAnalysis.filter(factor => factor.factor_id === activeFactor).map((factor) => (
              <div key={factor.factor_id} className="mb-6">
                <h4 className="text-lg font-medium mb-3">{factor.factor_name}敏感度分析</h4>
                
                <div className="h-64 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={factor.scenarios}
                      margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="value" 
                        tickFormatter={(value) => `${value}${factor.unit}`}
                        label={{ value: factor.factor_name, position: 'bottom', offset: 0 }}
                      />
                      <YAxis 
                        tickFormatter={(value) => formatCurrency(value)}
                        label={{ value: '對現金流影響', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelFormatter={(value) => `${factor.factor_name}: ${value}${factor.unit}`}
                      />
                      <Bar 
                        dataKey="impact" 
                        name="現金流影響" 
                        fill={(data) => data.impact > 0 ? '#82ca9d' : data.impact < 0 ? '#ff8042' : '#8884d8'}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-3">
                  {factor.scenarios.map((scenario, index) => (
                    <div 
                      key={index} 
                      className={`p-4 rounded-lg ${scenario.impact > 0 ? 'bg-green-50' : scenario.impact < 0 ? 'bg-red-50' : 'bg-gray-50'}`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className={`p-2 rounded-full mr-3 ${scenario.impact > 0 ? 'bg-green-100' : scenario.impact < 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                            {scenario.impact > 0 ? 
                              <ArrowUp className="h-5 w-5 text-green-600" /> : 
                              scenario.impact < 0 ? 
                              <ArrowDown className="h-5 w-5 text-red-600" /> : 
                              <Activity className="h-5 w-5 text-gray-600" />}
                          </div>
                          <div>
                            <p className="font-medium">{factor.factor_name}: {scenario.value}{factor.unit}</p>
                            <p className="text-sm text-gray-600">{scenario.description}</p>
                          </div>
                        </div>
                        <div className={`font-bold ${scenario.impact > 0 ? 'text-green-600' : scenario.impact < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {scenario.impact > 0 ? '+' : ''}{formatCurrency(scenario.impact)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-6 p-4 rounded-lg bg-blue-50">
              <h4 className="text-lg font-medium mb-2">敏感度分析建議</h4>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>根據銷售增長率的高敏感度，建議加強銷售預測和市場分析，以更準確地預測現金流。</li>
                <li>原材料成本對現金流有顯著影響，應考慮實施成本控制措施和探索替代供應商。</li>
                <li>改善應收賬款收款流程可以顯著提高現金流，建議審查信貸政策和收款程序。</li>
                <li>考慮對匯率波動進行對沖，以減輕外匯風險對現金流的潛在負面影響。</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CashFlowForecastDashboard;
